import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import {
  IRecordEventData,
  AppSubjectEnum,
  ModuleRecordCountDto,
  UserActionEnum,
} from '@splitsecondconnect/ultramarine-shared';
import { catchError, EMPTY, finalize, Subscription, tap } from 'rxjs';
import { ModulesEnum } from '../enums/modules.enum';
import { mapModuleToSubject } from '../helpers/map-module-to-subject';
import { SummaryService } from '../services/summary.service';
import { WebsocketService } from '../services/websocket.service';

interface SummaryState {
  isLoading: boolean;
  error: string | null;
  counts: ModuleRecordCountDto[];
}

const initialState: SummaryState = {
  isLoading: false,
  error: null,
  counts: [],
};

export const SummaryStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store) => {
    const countBySubject = computed(
      (): Partial<Record<AppSubjectEnum, number>> => {
        const lookup: Partial<Record<AppSubjectEnum, number>> = {};
        for (const item of store.counts()) {
          lookup[item.subject] = item.count;
        }
        return lookup;
      },
    );

    return {
      countBySubject,
      countByModule: computed((): Partial<Record<ModulesEnum, number>> => {
        const bySubject = countBySubject();
        const result: Partial<Record<ModulesEnum, number>> = {};
        for (const module of Object.values(ModulesEnum)) {
          try {
            const count = bySubject[mapModuleToSubject(module)];
            if (count != null) {
              result[module] = count;
            }
          } catch {
            // module not summary-backed
          }
        }
        return result;
      }),
    };
  }),
  withMethods(
    (
      store,
      service = inject(SummaryService),
      websocket = inject(WebsocketService),
    ) => ({
      subscribeToRecordEvents() {
        return websocket
          .onMultipleRecord(Object.values(ModulesEnum))
          .subscribe((event) => {
            if (
              event.action !== UserActionEnum.CREATE &&
              event.action !== UserActionEnum.DELETE
            ) {
              return;
            }

            patchState(store, {
              counts: upsertCountDelta(
                store.counts(),
                event.subject,
                event.action === UserActionEnum.CREATE ? 1 : -1,
              ),
            });
          });
      },
    loadCounts() {
      patchState(store, { isLoading: true, error: null });
      return service.getCounts().pipe(
        tap((res) => {
          patchState(store, { counts: res.data ?? [] });
        }),
        catchError((error) => {
          patchState(store, { error: error.message });
          return EMPTY;
        }),
        finalize(() => patchState(store, { isLoading: false })),
      );
    },
    }),
  ),
  withHooks((store) => {
    let recordEventsSub: Subscription | null = null;

    return {
      onInit() {
        recordEventsSub = store.subscribeToRecordEvents();
      },
      onDestroy() {
        recordEventsSub?.unsubscribe();
        recordEventsSub = null;
      },
    };
  }),
);

function upsertCountDelta(
  counts: ModuleRecordCountDto[],
  subject: IRecordEventData['subject'],
  delta: 1 | -1,
): ModuleRecordCountDto[] {
  const index = counts.findIndex((item) => item.subject === subject);

  if (index === -1) {
    return delta > 0 ? [...counts, { subject, count: 1 }] : counts;
  }

  return counts.map((item, currentIndex) =>
    currentIndex === index
      ? { ...item, count: Math.max(0, item.count + delta) }
      : item,
  );
}
